/**
 * SentDocumentStatusCell
 *
 * Renders a compound status column for the sent documents DataGrid.
 * Shows channel badges (SMS/Email) with meta-status colors and raw status tooltips,
 * plus a form lifecycle badge when applicable.
 */

import React from 'react';
import { Chip, CompoundChip } from '@jotpsych/ui';
import { Tooltip } from '@mui/material';
import { Email as EmailIcon, Sms as SmsIcon } from '@mui/icons-material';

import type {
  ChannelDeliveryInfo,
  SentDocumentGroup,
  DeliveryStatus,
  MetaDeliveryStatus,
} from '../types';
import { META_STATUS_LABELS, META_STATUS_MAP } from '../types';

type ChipColor = 'default' | 'info' | 'success' | 'error' | 'warning' | 'primary' | 'secondary';

function getMetaChipColor(meta: MetaDeliveryStatus): ChipColor {
  switch (meta) {
    case 'pending':
      return 'default';
    case 'delivered':
      return 'info';
    case 'failed':
      return 'error';
  }
}

function getFormStatusChipColor(status: string): ChipColor {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'in_progress':
      return 'info';
    case 'completed':
      return 'success';
    default:
      return 'default';
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function formatFormStatusLabel(status: string): string {
  switch (status) {
    case 'pending':
      return 'Form Pending';
    case 'in_progress':
      return 'Form In Progress';
    case 'completed':
      return 'Form Completed';
    default:
      return `Form ${status}`;
  }
}

function ChannelBadge({ channelStatus }: { channelStatus: ChannelDeliveryInfo }) {
  const channelIcon = channelStatus.channel === 'sms' ? <SmsIcon /> : <EmailIcon />;
  const rawStatus = channelStatus.status as DeliveryStatus;
  const meta = META_STATUS_MAP[rawStatus] ?? 'pending';
  const color = getMetaChipColor(meta);
  const channelLabel = channelStatus.channel === 'sms' ? 'SMS' : 'Email';
  const tooltipText = `${channelLabel}: ${capitalize(rawStatus)}`;

  return (
    <Tooltip title={tooltipText} arrow>
      <CompoundChip
        label={META_STATUS_LABELS[meta]}
        size="small"
        color={color}
        variant="filled"
        secondary={channelIcon}
        secondaryPlacement="start"
        sx={{ maxWidth: 'fit-content', lineHeight: 'normal' }}
      />
    </Tooltip>
  );
}

/** Renders a single channel badge (SMS or Email) for a communication group, or nothing. */
export function SingleChannelBadge({
  group,
  channel,
}: {
  group: SentDocumentGroup;
  channel: 'sms' | 'email';
}) {
  const channelStatus = group.channels.find(ch => ch.channel === channel);
  if (!channelStatus) return null;
  return <ChannelBadge channelStatus={channelStatus} />;
}

/** Renders the form lifecycle badge for a communication group, or nothing. */
export function FormStatusBadge({ group }: { group: SentDocumentGroup }) {
  if (!group.form_status) return null;
  return (
    <Chip
      label={formatFormStatusLabel(group.form_status.status)}
      size="small"
      color={getFormStatusChipColor(group.form_status.status)}
      variant="outlined"
      sx={{ maxWidth: 'fit-content', lineHeight: 'normal' }}
    />
  );
}
